<template>
  <div>
    <div>
      <input
        ref="input"
        :class="`form-control ${inputClass}`"
        :placeholder="placeholder"
        :aria-label="placeholder"
        :value="inputValue"
        @focus="isFocused = true"
        @blur="handleBlur"
        @input="handleInput($event.target.value)"
        autocomplete="off"
        :required="required"

        @keydown.up="handleUpKey"
        @keydown.down="handleDownKey"
        @keydown.enter="handleEnterKey"
        @keydown.esc="stopSuggestionSelection"
      />
    </div>
    <vue-bootstrap-typeahead-list
      class="vbt-autcomplete-list"
      ref="list"
      v-show="isFocused && data.length > 0 && suggestionSelection"
      :query="value"
      :data="formattedData"
      :activeSuggestion="activeSuggestion"
      :background-variant="backgroundVariant"
      :text-variant="textVariant"
      :maxMatches="maxMatches"
      :minMatchingChars="minMatchingChars"
      @hit="handleHit"
      @mouse-over-suggestion="mouseoverSuggestionHandler"
      @mouse-out-suggestion="mouseoutSuggestionHandler"
    >
      <!-- pass down all scoped slots -->
      <template v-for="(slot, slotName) in $scopedSlots" :slot="slotName" slot-scope="{ data, htmlText }">
        <slot :name="slotName" v-bind="{ data, htmlText }"></slot>
      </template>
      <!-- below is the right solution, however if the user does not provide a scoped slot, vue will still set $scopedSlots.suggestion to a blank scope
      <template v-if="$scopedSlots.suggestion" slot="suggestion" slot-scope="{ data, htmlText }">
        <slot name="suggestion" v-bind="{ data, htmlText }" />
      </template>-->
    </vue-bootstrap-typeahead-list>
  </div>
</template>

<script>
import VueBootstrapTypeaheadList from './VueBootstrapTypeaheadList.vue';
import ResizeObserver from 'resize-observer-polyfill';

export default {
  name: 'VueBootstrapTypehead',

  components: {
    VueBootstrapTypeaheadList,
  },

  props: {
    value: {
      type: String
    },
    data: {
      type: Array,
      required: true,
      validator: d => d instanceof Array
    },
    serializer: {
      type: Function,
      default: (d) => d,
      validator: d => d instanceof Function
    },
    backgroundVariant: String,
    textVariant: String,
    inputClass: {
      type: String,
      default: ''
    },
    maxMatches: {
      type: Number,
      default: 10
    },
    minMatchingChars: {
      type: Number,
      default: 2
    },
    placeholder: String,

    required: {
      type: Boolean,
      default: false,
    },

    autofocus: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      isFocused: false,
      inputValue: '',
      activeSuggestion: null,
      suggestionSelection: false,
    }
  },

  computed: {
    formattedData() {
      if (!(this.data instanceof Array)) {
        return []
      }
      return this.data.map((d, i) => {
        return {
          id: i,
          data: d,
          text: this.serializer(d)
        }
      })
    }
  },

  methods: {
    resizeList(el) {
      const rect = el.getBoundingClientRect()
      const listStyle = this.$refs.list.$el.style

      // Set the width of the list on resize
      listStyle.width = rect.width + 'px'
    },

    handleHit(evt) {
      if (typeof this.value !== 'undefined') {
        this.$emit('input', evt.text)
      }

      this.inputValue = evt.text
      this.$emit('hit', evt.data)
      this.$refs.input.blur()
      this.isFocused = false
    },

    handleBlur(evt) {
      const tgt = evt.relatedTarget
      if (tgt && tgt.classList.contains('vbst-item')) {
        return
      }
      this.isFocused = false
    },

    handleInput(newValue) {
      this.inputValue = newValue;

      // If v-model is being used, emit an input event
      if (typeof this.value !== 'undefined') {
        this.$emit('input', newValue.trim());
      }

      this.activeSuggestion = null;
      this.suggestionSelection = true;
    },

    handleUpKey(event) {
      // Prevent default behavior (monving cursor at the beginning of text field).
      // Cursor position must be preserved.
      event.preventDefault();

      this.suggestionSelection = true;
      this.selectPrevSuggestion();
    },

    selectPrevSuggestion() {
      const prevSuggestion = this.getPrevSuggestion();
      this.activeSuggestion = prevSuggestion;
    },

    getPrevSuggestion() {
      if(this.data.length === 0) {
        return null;
      }
      if(this.activeSuggestion === null) {
        return this.data[this.data.length-1];
      }

      const index = this.data.indexOf(this.activeSuggestion);
      if(index === -1) {
        return this.data[this.data.length-1];
      }
      if(index === 0) {
        return null;
      }

      const prevIndex = (index === 0) ? this.data.length-1 : index-1;
      return this.data[prevIndex];
    },

    handleDownKey(event) {
      // Prevent default behavior (monving cursor at the end of text field).
      // Cursor position must be preserved.
      event.preventDefault();

      this.suggestionSelection = true;
      this.selectNextSuggestion();
    },

    selectNextSuggestion() {
      const nextSuggestion = this.getNextSuggestion();
      if(nextSuggestion !== null) {
        this.activeSuggestion = nextSuggestion;
      }
    },

    getNextSuggestion() {
      if(this.data.length === 0) {
        return null;
      }
      if(this.activeSuggestion === null) {
        return this.data[0];
      }
      
      const index = this.data.indexOf(this.activeSuggestion);
      if(index === -1) {
        return this.data[0];
      }
      
      const nextIndex = (index === this.data.length-1) ? 0 : index+1;
      return this.data[nextIndex];
    },

    handleEnterKey(event) {
      if(!this.suggestionSelection) {
        return;
      }

      // Suggestion selection mode
      event.preventDefault();

      if(this.activeSuggestion !== null) {
        this.inputValue = this.activeSuggestion;
        this.$emit('input', this.activeSuggestion)
        this.stopSuggestionSelection();
      }
    },

    stopSuggestionSelection() {
      this.suggestionSelection = false;
      this.activeSuggestion = null;
    },

    mouseoverSuggestionHandler(suggestion) {
      this.activeSuggestion = suggestion;
    },

    mouseoutSuggestionHandler(suggestion) {
      if(this.activeSuggestion === suggestion) {
        this.activeSuggestion = null;
      }
    }
  },

  mounted() {
    this.$_ro = new ResizeObserver(() => {
      this.resizeList(this.$refs.input)
    })
    this.$_ro.observe(this.$refs.input)
    this.$_ro.observe(this.$refs.list.$el)

    if(this.autofocus) {
      this.$refs.input.focus();
    }
  },

  beforeDestroy() {
    this.$_ro.disconnect()
  }
}
</script>

<style scoped>
  .vbt-autcomplete-list {
    padding-top: 5px;
    position: absolute;
    max-height: 350px;
    overflow-y: auto;
    z-index: 999;
  }
</style>
